export type CourageMode = 'signed' | 'reveal_when_ready';
export type DeliveryMode = 'link' | 'email_relay';
export type RevealPolicy = 'never' | 'after_days' | 'after_reply';

export interface CourageModeData {
  courageMode: CourageMode;
  deliveryMode: DeliveryMode;
  revealPolicy: RevealPolicy;
  revealAfterDays: number | null;
  recipientEmail: string;
}

export const INITIAL_COURAGE_DATA: CourageModeData = {
  courageMode: 'signed',
  deliveryMode: 'link',
  revealPolicy: 'never',
  revealAfterDays: null,
  recipientEmail: '',
};

export type UserPlanTier = 'free' | 'starter' | 'connection' | 'legacy';

export function getPlanTier(highestBundle: string | null, hasPurchased: boolean): UserPlanTier {
  if (!hasPurchased || !highestBundle) return 'free';
  switch (highestBundle) {
    case 'starter': return 'starter';
    case 'connection': return 'connection';
    case 'legacy': return 'legacy';
    default: return 'free';
  }
}

/** @deprecated Quiet Courage removed – kept for type compat */
export function canUseQuietCourage(_tier: UserPlanTier): boolean {
  return false;
}

export function canUseRevealWhenReady(tier: UserPlanTier): boolean {
  return tier === 'legacy';
}

// ── Content moderation filter ──────────────────────────────────
// Context-aware: protects emotional, spiritual, and heartfelt expression.
// Only ACTUAL threats with hostile intent are blocked. Ambiguous phrases
// require corroborating hostile context AND no positive/emotional context.

// Unambiguous, near-universally hostile phrases — always block.
const THREAT_KEYWORDS = [
  "i'm going to kill you", 'im going to kill you', 'going to kill you',
  "i'll kill you", 'ill kill you', 'gonna kill you',
  "i'm going to hurt you", 'going to hurt you', "i'll hurt you",
  'i will hurt you', 'i will kill you',
  "i'm coming after you", 'coming after you to hurt',
  'find you and hurt', 'find you and kill',
  'know where you live and', 'i will find you and',
  'blow your head', 'blow up your', 'pipe bomb you',
  'shoot you dead', 'stab you', 'strangle you',
  'you are dead to me and i will', 'you will pay with your',
  'beat you to death', 'beat the shit out of you',
];

const HARASSMENT_KEYWORDS = [
  'you deserve to die', 'go die', 'go kill yourself',
  'kys', 'neck yourself', 'end yourself',
  'nobody loves you', 'everyone hates you',
  'worthless', 'pathetic loser', 'piece of shit',
  'you disgust me', 'fat pig', 'ugly bitch',
  'dumb bitch', 'stupid bitch', 'whore', 'slut',
  'trash human', 'waste of space', 'waste of life',
  'die alone', 'should be ashamed',
];

const HATE_SPEECH_KEYWORDS = [
  'nigger', 'nigga', 'faggot', 'fag', 'tranny',
  'retard', 'retarded', 'spic', 'chink', 'gook',
  'kike', 'wetback', 'beaner', 'towelhead', 'raghead',
  'coon', 'darkie', 'half breed', 'white power',
  'heil hitler', 'gas the', 'lynch',
  'go back to your country', 'subhuman',
];

const ILLEGAL_ACTIVITY_KEYWORDS = [
  'child porn', 'cp link', 'underage', 'sex with minor',
  'buy drugs', 'sell drugs', 'meth', 'cocaine for sale',
  'hire hitman', 'hitman for hire',
  'bomb threat', 'bomb plan', 'pipe bomb',
  'credit card number', 'social security number',
  'blackmail', 'extort', 'ransom',
];

const MISLEADING_HARMFUL_KEYWORDS = [
  'fake kidnapping', 'fake emergency', 'fake death',
  'pretend someone died', 'prank death', 'swatting',
  'dox', 'doxxing', 'leaked address', 'leaked phone',
  'revenge porn', 'leaked nudes', 'expose you',
];

const ALL_BLOCKED_PHRASES = [
  ...THREAT_KEYWORDS,
  ...HARASSMENT_KEYWORDS,
  ...HATE_SPEECH_KEYWORDS,
  ...ILLEGAL_ACTIVITY_KEYWORDS,
  ...MISLEADING_HARMFUL_KEYWORDS,
];

export type ModerationCategory =
  | 'threat'
  | 'harassment'
  | 'hate_speech'
  | 'illegal'
  | 'misleading'
  | 'clean';

export interface ModerationResult {
  blocked: boolean;
  category: ModerationCategory;
  matchedPhrase?: string;
  matchedSentence?: string;
  reason?: string;
  suggestion?: string;
  confidence?: 'low' | 'medium' | 'high';
}

function testPhrase(text: string, phrase: string): boolean {
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, 'i').test(text);
}

// Positive / emotional / spiritual context — when present, ambiguous phrasing
// is interpreted as heartfelt, not hostile.
const POSITIVE_CONTEXT_KEYWORDS = [
  'love', 'loved', 'loving', 'grateful', 'gratitude', 'thank', 'thanks', 'thankful',
  'blessing', 'blessed', 'bless', 'pray', 'prayer', 'praying', 'faith', 'faithful',
  'god', 'jesus', 'yah', 'yahshua', 'lord', 'holy', 'spirit', 'soul', 'heaven',
  'heal', 'healing', 'healed', 'forgive', 'forgiveness', 'peace', 'hope', 'hopeful',
  'cherish', 'precious', 'beautiful', 'kind', 'kindness', 'gentle', 'tender',
  'family', 'mother', 'mom', 'mama', 'father', 'dad', 'papa', 'son', 'daughter',
  'sister', 'brother', 'friend', 'wife', 'husband', 'grandma', 'grandpa',
  'proud', 'admire', 'inspire', 'inspired', 'inspiring', 'strength', 'strong',
  'courage', 'brave', 'overcome', 'overcame', 'transform', 'transformed', 'journey',
  'memory', 'memories', 'remember', 'celebrate', 'honor', 'tribute', 'legacy',
  'sorry', 'apolog', 'miss you', 'miss her', 'miss him', 'thinking of you',
  'heart', 'hearts', 'dear', 'sweet', 'beloved', 'angel', 'gift',
];

// Hostile intent corroborators — required alongside ambiguous phrasing
// before we treat it as a real threat.
const HOSTILE_INTENT_KEYWORDS = [
  'kill', 'murder', 'die', 'dead', 'hurt', 'harm', 'attack', 'destroy you',
  'beat you', 'gun', 'knife', 'blood', 'bleed', 'rape',
  'fuck you up', 'fuck you', 'bitch', 'asshole', 'motherfucker',
  'revenge', 'vengeance', 'punish you', 'make you suffer',
];

function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?\n])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function hasPositiveContext(text: string): boolean {
  const lower = text.toLowerCase();
  return POSITIVE_CONTEXT_KEYWORDS.some((kw) => testPhrase(lower, kw));
}

function hasHostileContext(text: string): boolean {
  const lower = text.toLowerCase();
  return HOSTILE_INTENT_KEYWORDS.some((kw) => testPhrase(lower, kw));
}

function findMatchingSentence(text: string, phrase: string): string | undefined {
  const sentences = splitSentences(text);
  return sentences.find((s) => testPhrase(s.toLowerCase(), phrase));
}

function buildSuggestion(category: ModerationCategory): string {
  switch (category) {
    case 'threat':
      return 'Try rephrasing without language that could read as a direct threat. Speak from love or memory instead.';
    case 'harassment':
      return 'Consider expressing how you feel rather than using words that demean the recipient.';
    case 'hate_speech':
      return 'Please remove slurs or language targeting a group. Speak to the person, not a label.';
    case 'illegal':
      return 'Please remove references to illegal activity.';
    case 'misleading':
      return 'Please remove content that could mislead or harm the recipient.';
    default:
      return 'Please revise your message.';
  }
}

export function moderateContent(text: string): ModerationResult {
  const lower = text.toLowerCase();

  // 1) Hate speech, illegal, misleading — always hard-block (slurs/illegal acts
  //    are not context-dependent).
  for (const kw of HATE_SPEECH_KEYWORDS) {
    if (testPhrase(lower, kw)) {
      return {
        blocked: true,
        category: 'hate_speech',
        matchedPhrase: kw,
        matchedSentence: findMatchingSentence(text, kw),
        reason: 'This phrase is a slur or hate-speech term.',
        suggestion: buildSuggestion('hate_speech'),
        confidence: 'high',
      };
    }
  }
  for (const kw of ILLEGAL_ACTIVITY_KEYWORDS) {
    if (testPhrase(lower, kw)) {
      return {
        blocked: true,
        category: 'illegal',
        matchedPhrase: kw,
        matchedSentence: findMatchingSentence(text, kw),
        reason: 'This references an illegal activity.',
        suggestion: buildSuggestion('illegal'),
        confidence: 'high',
      };
    }
  }
  for (const kw of MISLEADING_HARMFUL_KEYWORDS) {
    if (testPhrase(lower, kw)) {
      return {
        blocked: true,
        category: 'misleading',
        matchedPhrase: kw,
        matchedSentence: findMatchingSentence(text, kw),
        reason: 'This could mislead or harm the recipient.',
        suggestion: buildSuggestion('misleading'),
        confidence: 'high',
      };
    }
  }

  // 2) Threats / harassment — context-aware. We require both an unambiguous
  //    phrase match AND no overriding positive/spiritual context. The keyword
  //    lists above have been narrowed to phrases that are themselves
  //    unambiguous, so a positive halo only down-grades to "review" via low
  //    confidence — it does not allow real threats.
  for (const kw of THREAT_KEYWORDS) {
    if (testPhrase(lower, kw)) {
      const sentence = findMatchingSentence(text, kw) || text;
      const positive = hasPositiveContext(sentence);
      // Only block when sentence-level context is hostile or neutral.
      if (!positive) {
        return {
          blocked: true,
          category: 'threat',
          matchedPhrase: kw,
          matchedSentence: sentence,
          reason: 'This sentence reads as a direct threat of harm.',
          suggestion: buildSuggestion('threat'),
          confidence: 'high',
        };
      }
      // Positive halo around an unambiguous threat phrase is rare; flag for
      // soft review rather than hard block.
      return {
        blocked: false,
        category: 'threat',
        matchedPhrase: kw,
        matchedSentence: sentence,
        reason: 'Possibly hostile language detected, but context appears emotional.',
        suggestion: buildSuggestion('threat'),
        confidence: 'low',
      };
    }
  }

  for (const kw of HARASSMENT_KEYWORDS) {
    if (testPhrase(lower, kw)) {
      const sentence = findMatchingSentence(text, kw) || text;
      const hostile = hasHostileContext(sentence);
      const positive = hasPositiveContext(sentence);
      if (hostile || !positive) {
        return {
          blocked: true,
          category: 'harassment',
          matchedPhrase: kw,
          matchedSentence: sentence,
          reason: 'This sentence reads as harassment or abuse.',
          suggestion: buildSuggestion('harassment'),
          confidence: hostile ? 'high' : 'medium',
        };
      }
      return {
        blocked: false,
        category: 'harassment',
        matchedPhrase: kw,
        matchedSentence: sentence,
        reason: 'Potentially harsh language, but context appears emotional.',
        suggestion: buildSuggestion('harassment'),
        confidence: 'low',
      };
    }
  }

  return { blocked: false, category: 'clean', confidence: 'high' };
}

/** Backward-compatible wrapper */
export function containsBlockedContent(text: string): boolean {
  return moderateContent(text).blocked;
}

